import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

interface LenderProfileFormProps {
  lender?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

// Validation schema
const lenderProfileSchema = z.object({
  lender_name: z.string()
    .trim()
    .min(1, "Lender Name is required")
    .max(200, "Lender Name must be less than 200 characters"),
  short_name: z.string()
    .trim()
    .max(100, "Short Name must be less than 100 characters")
    .optional()
    .or(z.literal("")),
  legal_contact: z.string()
    .trim()
    .max(200, "Legal Contact must be less than 200 characters")
    .optional()
    .or(z.literal("")),
  legal_contact_phone: z.string()
    .trim()
    .regex(/^[0-9+\-\s()]*$/, "Invalid phone number format")
    .max(20, "Phone number must be less than 20 characters")
    .optional()
    .or(z.literal("")),
  legal_contact_email: z.string()
    .trim()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .optional()
    .or(z.literal("")),
});

export const LenderProfileForm = ({
  lender,
  onSuccess,
  onCancel,
}: LenderProfileFormProps) => {
  const [formData, setFormData] = useState({
    lender_name: "",
    short_name: "",
    legal_contact: "",
    legal_contact_phone: "",
    legal_contact_email: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (lender) {
      setFormData({
        lender_name: lender.lender_name || "",
        short_name: lender.short_name || "",
        legal_contact: lender.legal_contact || "",
        legal_contact_phone: lender.legal_contact_phone || "",
        legal_contact_email: lender.legal_contact_email || "",
      });
    }
  }, [lender]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      // Validate form data
      const validatedData = lenderProfileSchema.parse(formData);

      // Convert empty strings to null for optional fields
      const dataToSubmit = {
        lender_name: validatedData.lender_name,
        short_name: validatedData.short_name || null,
        legal_contact: validatedData.legal_contact || null,
        legal_contact_phone: validatedData.legal_contact_phone || null,
        legal_contact_email: validatedData.legal_contact_email || null,
      };

      if (lender) {
        const { error } = await supabase
          .from("lender_profiles")
          .update(dataToSubmit)
          .eq("id", lender.id);
        if (error) throw error;
        toast({ title: "Lender updated successfully" });
      } else {
        const { error } = await supabase
          .from("lender_profiles")
          .insert(dataToSubmit);
        if (error) throw error;
        toast({ title: "Lender created successfully" });
      }

      onSuccess();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        // Handle validation errors
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
        toast({
          title: "Validation Error",
          description: "Please check the form fields",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="lender_name">Lender Name *</Label>
        <Input
          id="lender_name"
          placeholder="Enter Legal Name"
          value={formData.lender_name}
          onChange={(e) =>
            setFormData({ ...formData, lender_name: e.target.value })
          }
          required
        />
        {errors.lender_name && (
          <p className="text-sm text-red-600 mt-1">{errors.lender_name}</p>
        )}
      </div>

      <div>
        <Label htmlFor="short_name">Short Name</Label>
        <Input
          id="short_name"
          placeholder="Enter Name Used Internally"
          value={formData.short_name}
          onChange={(e) =>
            setFormData({ ...formData, short_name: e.target.value })
          }
        />
        {errors.short_name && (
          <p className="text-sm text-red-600 mt-1">{errors.short_name}</p>
        )}
      </div>

      <div>
        <Label htmlFor="legal_contact">Legal Contact</Label>
        <Input
          id="legal_contact"
          placeholder="First, Last"
          value={formData.legal_contact}
          onChange={(e) =>
            setFormData({ ...formData, legal_contact: e.target.value })
          }
        />
        {errors.legal_contact && (
          <p className="text-sm text-red-600 mt-1">{errors.legal_contact}</p>
        )}
      </div>

      <div>
        <Label htmlFor="legal_contact_phone">Legal Contact Phone Number</Label>
        <Input
          id="legal_contact_phone"
          placeholder="Enter Number"
          value={formData.legal_contact_phone}
          onChange={(e) =>
            setFormData({ ...formData, legal_contact_phone: e.target.value })
          }
        />
        {errors.legal_contact_phone && (
          <p className="text-sm text-red-600 mt-1">{errors.legal_contact_phone}</p>
        )}
      </div>

      <div>
        <Label htmlFor="legal_contact_email">Legal Contact Email</Label>
        <Input
          id="legal_contact_email"
          type="email"
          placeholder="lender@email.com"
          value={formData.legal_contact_email}
          onChange={(e) =>
            setFormData({ ...formData, legal_contact_email: e.target.value })
          }
        />
        {errors.legal_contact_email && (
          <p className="text-sm text-red-600 mt-1">{errors.legal_contact_email}</p>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : lender ? "Update" : "Create"}
        </Button>
      </div>
    </form>
  );
};
